import { useCallback, useEffect, useMemo, useState } from 'react'
import { z } from 'zod'

const WIDTH_STORAGE_KEY = 'gecko_documentDetails_metadataPanelWidth:v1'
const HEIGHT_STORAGE_KEY = 'gecko_documentDetails_metadataPanelHeight:v1'
export const DOCUMENT_VIEWER_PANEL_ID = 'document-viewer'
export const DOCUMENT_METADATA_PANEL_ID = 'document-metadata'

const DEFAULT_METADATA_PANE_PERCENT = 25
const DEFAULT_MOBILE_METADATA_PANE_PERCENT = 45
const MIN_PANE_PERCENT = 20
const MAX_PANE_PERCENT = 80

const metadataPanePercentSchema = z.coerce
  .number()
  .min(MIN_PANE_PERCENT)
  .max(MAX_PANE_PERCENT)

function readStoredMetadataPanePercent(
  storageKey: string,
  fallback: number
): number {
  try {
    const result = metadataPanePercentSchema.safeParse(
      localStorage.getItem(storageKey)
    )
    return result.success ? result.data : fallback
  } catch {
    return fallback
  }
}

function writeStoredMetadataPanePercent(
  storageKey: string,
  percent: number
): void {
  try {
    localStorage.setItem(storageKey, String(percent))
  } catch {
    /* localStorage unavailable */
  }
}

export function useMetadataPanelLayout(
  orientation: 'horizontal' | 'vertical' = 'horizontal'
): {
  defaultLayout: Record<string, number>
  handleLayoutChanged: (nextLayout: Record<string, number>) => void
} {
  const [metadataPaneWidthPercent, setMetadataPaneWidthPercent] = useState(() =>
    readStoredMetadataPanePercent(
      WIDTH_STORAGE_KEY,
      DEFAULT_METADATA_PANE_PERCENT
    )
  )
  const [metadataPaneHeightPercent, setMetadataPaneHeightPercent] = useState(
    () =>
      readStoredMetadataPanePercent(
        HEIGHT_STORAGE_KEY,
        DEFAULT_MOBILE_METADATA_PANE_PERCENT
      )
  )

  useEffect(() => {
    writeStoredMetadataPanePercent(WIDTH_STORAGE_KEY, metadataPaneWidthPercent)
  }, [metadataPaneWidthPercent])

  useEffect(() => {
    writeStoredMetadataPanePercent(
      HEIGHT_STORAGE_KEY,
      metadataPaneHeightPercent
    )
  }, [metadataPaneHeightPercent])

  const metadataPanePercent =
    orientation === 'vertical'
      ? metadataPaneHeightPercent
      : metadataPaneWidthPercent

  const defaultLayout = useMemo(
    () => ({
      [DOCUMENT_VIEWER_PANEL_ID]: 100 - metadataPanePercent,
      [DOCUMENT_METADATA_PANEL_ID]: metadataPanePercent,
    }),
    [metadataPanePercent]
  )

  const handleLayoutChanged = useCallback(
    (nextLayout: Record<string, number>) => {
      const result = metadataPanePercentSchema.safeParse(
        nextLayout[DOCUMENT_METADATA_PANEL_ID]
      )
      if (result.success) {
        if (orientation === 'vertical') {
          setMetadataPaneHeightPercent(result.data)
        } else {
          setMetadataPaneWidthPercent(result.data)
        }
      }
    },
    [orientation]
  )

  return { defaultLayout, handleLayoutChanged }
}
